// Commission tier structures for different broker types
// Maximum commission cap per loan for all broker types
export const MAX_COMMISSION_PER_LOAN = 15000;

export const BROKER_TYPES = {
  equity: {
    name: 'Equity Partner',
    tiers: [
      { name: 'Tier 1', loans: 1, rate: 0.50 },   // 1-3 loans: 50%
      { name: 'Tier 2', loans: 4, rate: 0.60 },   // 4-6 loans: 60%
      { name: 'Tier 3', loans: 7, rate: 0.70 },   // 7+ loans: 70%
    ],
    selfGenRate: 0.80,
  },
  founding: {
    name: 'Founding Broker',
    tiers: [
      { name: 'Tier 1', loans: 1, rate: 0.25 },   // 1 loan: 25%
      { name: 'Tier 2', loans: 2, rate: 0.35 },   // 2 loans: 35%
      { name: 'Tier 3', loans: 3, rate: 0.45 },   // 3-4 loans: 45%
      { name: 'Tier 4', loans: 5, rate: 0.55 },   // 5-6 loans: 55%
      { name: 'Tier 5', loans: 7, rate: 0.65 },   // 7+ loans: 65%
    ],
    selfGenRate: 0.70,
  },
  executive: {
    name: 'Executive Broker',
    tiers: [
      { name: 'Tier 1', loans: 1, rate: 0.20 },   // 1 loan: 20%
      { name: 'Tier 2', loans: 2, rate: 0.30 },   // 2 loans: 30%
      { name: 'Tier 3', loans: 3, rate: 0.40 },   // 3-4 loans: 40%
      { name: 'Tier 4', loans: 5, rate: 0.50 },   // 5-6 loans: 50%
      { name: 'Tier 5', loans: 7, rate: 0.60 },   // 7+ loans: 60%
    ],
    selfGenRate: 0.70,
  },
  senior: {
    name: 'Senior Broker',
    tiers: [
      { name: 'Tier 1', loans: 1, rate: 0.15 },   // 1 loan: 15%
      { name: 'Tier 2', loans: 2, rate: 0.25 },   // 2 loans: 25%
      { name: 'Tier 3', loans: 3, rate: 0.35 },   // 3-4 loans: 35%
      { name: 'Tier 4', loans: 5, rate: 0.45 },   // 5-6 loans: 45%
      { name: 'Tier 5', loans: 7, rate: 0.55 },   // 7+ loans: 55%
    ],
    selfGenRate: 0.70,
  },
};

// Get broker type from localStorage or default to founding
export const getBrokerType = () => {
  const storedType = localStorage.getItem('kinfolk_broker_type');
  return storedType || 'founding';
};

// Set broker type in localStorage
export const setBrokerType = (type) => {
  localStorage.setItem('kinfolk_broker_type', type);
};

// Get commission tiers for the current broker type
export const getCommissionTiers = (brokerType = null) => {
  const type = brokerType || getBrokerType();
  return BROKER_TYPES[type] || BROKER_TYPES.founding;
};

// Get current tier based on loan count
export const getCurrentTier = (loanCount, brokerType = null) => {
  const config = getCommissionTiers(brokerType);
  const tiers = config.tiers;
  
  for (let i = tiers.length - 1; i >= 0; i--) {
    if (loanCount >= tiers[i].loans) {
      return tiers[i];
    }
  }
  return tiers[0];
};

// Get next tier
export const getNextTier = (loanCount, brokerType = null) => {
  const config = getCommissionTiers(brokerType);
  const currentTier = getCurrentTier(loanCount, brokerType);
  const tiers = config.tiers;
  const currentIndex = tiers.findIndex(t => t.name === currentTier.name);
  
  if (currentIndex < tiers.length - 1) {
    return tiers[currentIndex + 1];
  }
  return null;
};

// Calculate commission for a loan (with $15,000 cap)
export const calculateLoanCommission = (loan, totalClosedLoans, brokerType = null) => {
  if (loan.status !== 'Closed') return 0;
  
  const config = getCommissionTiers(brokerType);
  
  // Determine base commission based on commission type
  const commissionType = loan.commissionType || loan.commission_type || 'percent';
  const baseCommission = commissionType === 'flat'
    ? (loan.baseCommissionRate || loan.base_commission_rate || 0)
    : (loan.loanAmount || loan.loan_amount || 0) * ((loan.baseCommissionRate || loan.base_commission_rate || 2.75) / 100);
  
  // Determine tier rate based on total closed loan count
  const tierRate = getCurrentTier(totalClosedLoans, brokerType).rate;
  
  // If self-generated, use self-gen rate regardless of tier
  const finalRate = loan.isSelfGenerated ? config.selfGenRate : tierRate;
  
  // Calculate commission and apply cap
  const commission = baseCommission * finalRate;
  return Math.min(commission, MAX_COMMISSION_PER_LOAN);
};

// Calculate PROJECTED commission for in-process loans (ignores status check, with $15,000 cap)
export const calculateProjectedCommission = (loan, projectedTotalLoans, brokerType = null) => {
  const config = getCommissionTiers(brokerType);
  
  // Determine base commission based on commission type
  const commissionType = loan.commissionType || loan.commission_type || 'percent';
  const baseCommission = commissionType === 'flat'
    ? (loan.baseCommissionRate || loan.base_commission_rate || 0)
    : (loan.loanAmount || loan.loan_amount || 0) * (((loan.baseCommissionRate || loan.base_commission_rate || 2.75)) / 100);
  
  // Determine tier rate based on projected total loan count (closed + in-process)
  const tierRate = getCurrentTier(projectedTotalLoans, brokerType).rate;
  
  // If self-generated, use self-gen rate regardless of tier
  const finalRate = loan.isSelfGenerated ? config.selfGenRate : tierRate;
  
  // Calculate commission and apply cap
  const commission = baseCommission * finalRate;
  return Math.min(commission, MAX_COMMISSION_PER_LOAN);
};
